import { activityFlatsOfModels } from '@components/pages/activity-log/services/activity-log-flat.util';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BookingChain, BookingChainLink } from '@services/api/booking/booking-chain.model';
import { BookingChainErrorMapper } from '@services/api/booking/booking-chain-error.mapper';
import { IBookingModel } from '@services/api/booking/booking-model.interface';
import { LogService } from '@services/util/log.service';
import { format } from 'date-fns';
import { ConfirmationService, MessageService } from 'primeng/api';
import { BadgeModule } from 'primeng/badge';
// PrimeNG imports
import { ButtonModule } from 'primeng/button';
import { ConfirmDialogModule } from 'primeng/confirmdialog';
import { DialogModule } from 'primeng/dialog';
import { MessageModule } from 'primeng/message';
import { SkeletonModule } from 'primeng/skeleton';
import { TabsModule } from 'primeng/tabs';
import { ToastModule } from 'primeng/toast';
import { Subject, takeUntil } from 'rxjs';
import { CommonModule } from '@angular/common';
import { Component, HostListener, inject, OnDestroy, OnInit } from '@angular/core';
import { ActivatedRoute, Router } from '@angular/router';
// Custom components
import { BookingBillingTabComponent } from './components/booking-billing-tab/booking-billing-tab.component';
import { BookingChainLinkDialogComponent } from './components/booking-chain-link-dialog/booking-chain-link-dialog.component';
import { BookingChainStripComponent } from './components/booking-chain-strip/booking-chain-strip.component';
import { BookingGeneralTabComponent } from './components/booking-general-tab/booking-general-tab.component';
import { BookingPageHeaderComponent } from './components/booking-page-header/booking-page-header.component';
import { BookingPersonalTabComponent } from './components/booking-personal-tab/booking-personal-tab.component';
import { BookingQuickInfoComponent } from './components/booking-quick-info/booking-quick-info.component';
import { BookingRoomsTabComponent } from './components/booking-rooms-tab/booking-rooms-tab.component';
import { HostActivityLogService } from '../shared/host-activity-log.service';
import { HostBookingSource } from '../shared/host-activity.model';
import {
  bookingSaveEvents,
  chainMetadata,
  HostBookingSnapshot,
  hostBookingSnapshotOf,
  hostBookingSourceOf,
  HostFieldChange
} from '../shared/host-activity.metadata';
import { BookingActionEvent } from './models/booking-detail.model';
import { BookingChangeTrackerService, FieldChange } from './services/booking-change-tracker.service';
import { BookingChainLinkService } from './services/booking-chain-link.service';
import { BookingDetailService } from './services/booking-detail.service';

@Component({
  selector: 'app-booking-detail',
  standalone: true,
  imports: [
    CommonModule,
    TranslateModule,
    SkeletonModule,
    MessageModule,
    ButtonModule,
    TabsModule,
    BadgeModule,
    DialogModule,
    ConfirmDialogModule,
    ToastModule,
    BookingPageHeaderComponent,
    BookingQuickInfoComponent,
    BookingChainStripComponent,
    BookingChainLinkDialogComponent,
    BookingGeneralTabComponent,
    BookingPersonalTabComponent,
    BookingRoomsTabComponent,
    BookingBillingTabComponent
  ],
  providers: [ConfirmationService, MessageService],
  templateUrl: './booking-detail.component.html'
})
export class BookingDetailComponent implements OnInit, OnDestroy {
  activeTabIndex: number = 0;
  loading: boolean = false;
  isSaving: boolean = false;
  // Success message is now handled by MessageService
  unsavedChangesDialog: boolean = false;

  // Booking data populated from API
  booking: IBookingModel | null = null;

  chain: BookingChain | null = null;
  chainForDialog: BookingChain | null = null;
  linkDialogVisible = false;
  private openChainDialogOnLoad = false;

  // Change tracking
  changedFields: FieldChange[] = [];
  tabChangeCount: Record<string, number> = {
    general: 0,
    personal: 0,
    rooms: 0,
    billing: 0
  };

  // Map of tab indexes to names
  private tabIndexToName: Record<number, string> = {
    0: 'general',
    1: 'personal',
    2: 'rooms',
    3: 'billing'
  };

  // Subject for cleanup
  private destroy$ = new Subject<void>();

  // Pending navigation
  private pendingNavigation: { url: string } | null = null;

  private backUrl: string | null = null;
  private pendingOverviewUpdate: { id: number; email?: string; phone?: string } | null = null;
  /** Where this detail was opened from, for the host activity log (never the URL). */
  private activitySource: HostBookingSource = 'direct';

  // Use Angular's inject function (recommended in Angular 19+)
  private route = inject(ActivatedRoute);
  private router = inject(Router);
  private bookingDataService = inject(BookingDetailService);
  public changeTracker = inject(BookingChangeTrackerService);
  private confirmationService = inject(ConfirmationService);
  private logService = inject(LogService);
  private messageService = inject(MessageService);
  private translateService = inject(TranslateService);
  private readonly chainLinkService = inject(BookingChainLinkService);
  private readonly chainErrorMapper = inject(BookingChainErrorMapper);
  private readonly hostActivityLog = inject(HostActivityLogService);

  /**
   * Listen for browser navigation attempts and show confirmation dialog if there are unsaved changes
   */
  @HostListener('window:beforeunload', ['$event'])
  handleBeforeUnload(event: BeforeUnloadEvent): void {
    if (this.changeTracker.hasUnsavedChanges) {
      event.preventDefault();
      event.returnValue = ''; // This shows a standard browser confirmation dialog
      return;
    }
  }

  ngOnInit() {
    const incoming = history.state;
    const incomingSource = typeof incoming?.sourceUrl === 'string' ? incoming.sourceUrl : null;
    if (incoming?.from === 'booking-overview' && incomingSource?.startsWith('/booking-overview')) {
      this.backUrl = incomingSource;
    } else if (incoming?.from === 'bookings' && incomingSource?.startsWith('/bookings')) {
      this.backUrl = incomingSource;
    }
    this.openChainDialogOnLoad = incoming?.openChainDialog === true;
    this.activitySource = hostBookingSourceOf(incoming?.from);

    // Load booking data
    this.loading = true;
    this.route.params.subscribe((params) => {
      this.bookingDataService.getBooking(params['id']).subscribe({
        next: (booking: IBookingModel) => {
          this.booking = booking;

          this.bookingDataService
            .resolveChain(booking)
            .pipe(takeUntil(this.destroy$))
            .subscribe((chain) => {
              this.chainForDialog = chain;
              this.chain = chain.links.length > 1 ? chain : null;
              if (this.openChainDialogOnLoad) {
                this.linkDialogVisible = true;
                this.openChainDialogOnLoad = false;
              }
            });

          // Initialize change tracker with original booking
          this.changeTracker.initialize(booking);

          // Subscribe to changes
          this.changeTracker.changes$.pipe(takeUntil(this.destroy$)).subscribe((changes) => {
            this.changedFields = changes;
            this.updateTabChangeCount();
          });

          this.loading = false;
        },
        error: (err) => {
          this.logService.error('Error loading booking', err);
          this.loading = false;
          this.router.navigate(['/error']);
        }
      });
    });
  }

  ngOnDestroy() {
    this.destroy$.next();
    this.destroy$.complete();
  }

  /**
   * Handle actions from child components
   */
  onActionEvent(event: BookingActionEvent): void {
    switch (event.action) {
      case 'back':
        const targetUrl = this.backUrl ?? '/bookings';
        this.handleNavigationAttempt(targetUrl);
        break;
      case 'cancel':
        this.revertChanges();
        break;
      case 'save':
        this.saveBooking();
        break;
      case 'status-change':
        // Status changes are automatically tracked by the change tracker
        break;
      case 'field-change':
        // Track the changed field
        if (event.additionalData && event.additionalData.fieldName) {
          const tabName = this.tabIndexToName[this.activeTabIndex];
          this.trackFieldChange(event.additionalData.fieldName, event.additionalData.newValue, tabName);
        }
        break;
      default:
        this.logService.warn('Unhandled action:', event.action);
    }
  }

  onChainNavigate(bookingId: number): void {
    this.handleNavigationAttempt(`/bookings/${bookingId}`);
  }

  onChainLinkRequested(): void {
    this.linkDialogVisible = true;
  }

  onChainLinked(): void {
    this.reloadBooking();
  }

  onChainDetach(link: BookingChainLink): void {
    this.confirmationService.confirm({
      header: this.translateService.instant('booking.chain.detach.title'),
      message: this.translateService.instant('booking.chain.detach.message', {
        id: link.id,
        guest: link.guestName,
        start: format(link.startDate, 'yyyy-MM-dd'),
        end: format(link.endDate, 'yyyy-MM-dd'),
        previousId: this.previousIdOf(link)
      }),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('booking.chain.detach.confirm'),
      rejectLabel: this.translateService.instant('common.cancel'),
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => this.detachChainLink(link)
    });
  }

  /**
   * Track changes to a specific field
   */
  private trackFieldChange(fieldName: string, newValue: any, tabName?: string): void {
    this.changeTracker.trackChange(fieldName, newValue, tabName);
  }

  private reloadBooking(): void {
    const id = Number(this.route.snapshot.params['id']);
    this.bookingDataService
      .getBooking(id)
      .pipe(takeUntil(this.destroy$))
      .subscribe((booking) => {
        this.booking = booking;
        this.changeTracker.initialize(booking);
        this.bookingDataService
          .resolveChain(booking)
          .pipe(takeUntil(this.destroy$))
          .subscribe((chain) => {
            this.chainForDialog = chain;
            this.chain = chain.links.length > 1 ? chain : null;
          });
      });
  }

  private previousIdOf(link: BookingChainLink): number {
    const index = this.chain?.links.findIndex((candidate) => candidate.id === link.id) ?? -1;
    return index > 0 ? this.chain!.links[index - 1].id : 0;
  }

  private detachChainLink(link: BookingChainLink): void {
    const detached = chainMetadata({ bookingId: link.id, flats: activityFlatsOfModels(link.flats), previousId: this.previousIdOf(link) });
    this.chainLinkService.detach(link.id).subscribe({
      next: () => {
        this.hostActivityLog.log('chain_detach', detached);
        this.onDetachSuccess();
      },
      error: (error: unknown) => this.onDetachError(error)
    });
  }

  private onDetachSuccess(): void {
    this.messageService.add({
      severity: 'success',
      summary: this.translateService.instant('common.success'),
      detail: this.translateService.instant('booking.chain.success.detached'),
      life: 3000
    });
    this.reloadBooking();
  }

  private onDetachError(error: unknown): void {
    const mapped = this.chainErrorMapper.map(error);
    this.messageService.add({
      severity: 'error',
      summary: this.translateService.instant('common.error'),
      detail: [this.translateService.instant(mapped.messageKey), mapped.details].filter(Boolean).join(' '),
      life: 6000
    });
  }

  /**
   * Update tab change count indicators
   */
  private updateTabChangeCount(): void {
    for (const tab of Object.keys(this.tabChangeCount)) {
      this.tabChangeCount[tab] = this.changeTracker.getChangeCountByTab(tab);
    }
  }

  /**
   * Revert all changes back to the original state
   */
  private revertChanges(): void {
    if (!this.booking || !this.changeTracker.hasUnsavedChanges) return;

    try {
      // Get the original booking data from the change tracker
      const originalBooking = this.changeTracker.getOriginalBooking();

      // Replace the current booking with the original data
      this.booking = originalBooking;

      // Re-initialize the change tracker with the original booking data
      // This will reset all tracked changes
      this.changeTracker.initialize(originalBooking);

      // Show toast message
      this.messageService.add({
        severity: 'info',
        summary: this.translateService.instant('bookingDetail.revert.success'),
        detail: this.translateService.instant('bookingDetail.revert.successDetail'),
        life: 3000
      });
    } catch (err) {
      this.logService.error('Error reverting changes', err);
      this.messageService.add({
        severity: 'error',
        summary: this.translateService.instant('common.error'),
        detail: this.translateService.instant('bookingDetail.revert.error'),
        life: 5000
      });
    }
  }

  /**
   * Save booking changes to API
   */
  private saveBooking(): void {
    if (!this.booking) return;

    // The state before the save, for the host activity log
    const activityBefore = hostBookingSnapshotOf(this.changeTracker.getOriginalBooking());
    const activityChanges = [...this.changedFields];

    // Get only the changed fields
    const changedData = this.changeTracker.getChangedFieldsObject();

    // Don't proceed if there are no changes
    if (this.changedFields.length === 0) {
      this.messageService.add({
        severity: 'info',
        summary: this.translateService.instant('common.info'),
        detail: this.translateService.instant('bookingDetail.save.noChanges')
      });
      return;
    }

    // Ensure the id is present and is a number
    if (!changedData.id && this.booking?.id) {
      changedData.id = this.booking.id;
    }

    // Type check to ensure id is definitely a number
    if (typeof changedData.id !== 'number') {
      this.logService.error('Cannot save booking: missing or invalid ID');
      this.isSaving = false;
      return;
    }

    // Ensure dates are properly formatted
    // Use isDate helper to safely check
    const isDate = (value: any): value is Date => {
      return value !== null && value !== undefined && typeof value === 'object' && Object.prototype.toString.call(value) === '[object Date]';
    };

    if (changedData.start_date && isDate(changedData.start_date)) {
      changedData.start_date = format(changedData.start_date, 'yyyy-MM-dd');
    }

    if (changedData.end_date && isDate(changedData.end_date)) {
      changedData.end_date = format(changedData.end_date, 'yyyy-MM-dd');
    }

    this.isSaving = true;
    // Now TypeScript knows changedData.id is definitely a number
    this.bookingDataService.saveBooking(changedData as Partial<IBookingModel> & { id: number }).subscribe({
      next: (result) => {
        if (result) {
          this.logBookingSave({ before: activityBefore, changes: activityChanges, saved: result });
          // Show success toast message
          this.messageService.add({
            severity: 'success',
            summary: this.translateService.instant('common.success'),
            detail: this.translateService.instant('bookingDetail.save.success'),
            life: 3000
          });

          // Update local booking with saved data
          this.booking = result;

          if (typeof result.id === 'number') {
            this.pendingOverviewUpdate = {
              id: result.id,
              email: result.email_address ?? undefined,
              phone: result.phone_number ?? undefined
            };
          }

          // Reset change tracker with updated booking
          this.changeTracker.resetAfterSave(result);
        }
        this.isSaving = false;
      },
      error: (error) => {
        // Safely log error, ensuring we don't try to access properties of undefined
        this.logService.error('Error saving booking:', error ? error : 'Unknown error');

        // Show error toast with details if available
        let errorMessage = this.translateService.instant('bookingDetail.save.error');

        // Try to extract more specific error message if available
        if (error && error.error && typeof error.error === 'object') {
          if (error.error.violations) {
            // API validation errors
            const violations = error.error.violations;
            if (Array.isArray(violations) && violations.length > 0) {
              errorMessage = `${errorMessage}: ${violations.map((v) => v.message).join(', ')}`;
            }
          } else if (error.error.detail) {
            // Standard API error format
            errorMessage = `${errorMessage}: ${error.error.detail}`;
          } else if (error.error.hydra && error.error.hydra.description) {
            // Hydra API error format
            errorMessage = `${errorMessage}: ${error.error.hydra.description}`;
          }
        }

        this.messageService.add({
          severity: 'error',
          summary: this.translateService.instant('common.error'),
          detail: errorMessage,
          life: 5000
        });

        this.isSaving = false;
      }
    });
  }

  /** The status-change / save events of one successful save, from the state read before it and the saved booking. */
  private logBookingSave(input: { before: HostBookingSnapshot; changes: readonly HostFieldChange[]; saved: IBookingModel }): void {
    const { before, changes, saved } = input;
    const afterFlats = activityFlatsOfModels(saved.flats);
    const events = bookingSaveEvents({ before, changes, afterFlats, source: this.activitySource, now: new Date() });
    events.forEach((event) => this.hostActivityLog.log(event.action, event.metadata));
  }

  /**
   * Handle navigation attempt with unsaved changes check
   */
  private handleNavigationAttempt(url: string): void {
    if (this.changeTracker.hasUnsavedChanges) {
      this.pendingNavigation = { url };
      this.showUnsavedChangesDialog();
    } else {
      this.navigateBack(url);
    }
  }

  private navigateBack(url: string): void {
    const isOverview = url.startsWith('/booking-overview');
    if (isOverview || url.includes('?')) {
      this.router.navigateByUrl(url, isOverview ? this.buildOverviewNavExtras() : undefined);
    } else {
      this.router.navigate([url]);
    }
  }

  private buildOverviewNavExtras(): { state: { updatedBookingId: number; updatedFields: { email?: string; phone?: string } } } | undefined {
    if (!this.pendingOverviewUpdate) {
      return undefined;
    }
    return {
      state: {
        updatedBookingId: this.pendingOverviewUpdate.id,
        updatedFields: {
          email: this.pendingOverviewUpdate.email,
          phone: this.pendingOverviewUpdate.phone
        }
      }
    };
  }

  /**
   * Show confirmation dialog for unsaved changes
   */
  private showUnsavedChangesDialog(): void {
    this.confirmationService.confirm({
      header: this.translateService.instant('bookingDetail.unsavedChanges.header'),
      message: this.translateService.instant('bookingDetail.unsavedChanges.message'),
      icon: 'pi pi-exclamation-triangle',
      acceptLabel: this.translateService.instant('common.confirm'),
      rejectLabel: this.translateService.instant('common.cancel'),
      accept: () => {
        if (this.pendingNavigation) {
          this.navigateBack(this.pendingNavigation.url);
          this.pendingNavigation = null;
        }
      },
      reject: () => {
        this.pendingNavigation = null;
      }
    });
  }
}
